"use client";

import { useEffect, useRef, useState } from "react";

type Amenity = { name: string; icon?: string };

function Tile({ a }: { a: Amenity }) {
  return (
    <div className="flex w-36 shrink-0 flex-col items-center justify-center gap-3 rounded-2xl border border-brand-line bg-brand-surface px-3 py-7 text-center transition-colors hover:border-brand-accent md:w-44">
      {a.icon ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={a.icon}
            alt=""
            aria-hidden
            width={44}
            height={44}
            className="h-11 w-11 object-contain"
          />
        </>
      ) : null}
      <span className="text-sm font-medium leading-snug text-brand-ink">
        {a.name}
      </span>
    </div>
  );
}

// A single row of amenity tiles. When the tiles fit, they sit statically;
// when there are more than fit, the row auto-scrolls as a seamless marquee
// (the list is duplicated so a translateX(-50%) loop has no seam), pausing on
// hover and honouring reduced-motion.
export function AmenityTiles({ items }: { items: Amenity[] }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [marquee, setMarquee] = useState(false);

  useEffect(() => {
    const check = () => {
      const wrap = wrapRef.current;
      const track = trackRef.current;
      if (!wrap || !track) return;
      // Compare the single (non-duplicated) set width against the container.
      const setWidth = marquee ? track.scrollWidth / 2 : track.scrollWidth;
      setMarquee(setWidth > wrap.clientWidth + 1);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items, marquee]);

  if (!items.length) return null;

  if (!marquee) {
    return (
      <div ref={wrapRef} className="mt-8 overflow-hidden md:mt-10">
        <div ref={trackRef} className="flex gap-3 md:gap-4">
          {items.map((a) => (
            <Tile key={a.name} a={a} />
          ))}
        </div>
      </div>
    );
  }

  const loop = [...items, ...items];
  return (
    <div
      ref={wrapRef}
      className="group mt-8 overflow-hidden md:mt-10 [mask-image:linear-gradient(to_right,transparent,black_3%,black_97%,transparent)]"
    >
      <div
        ref={trackRef}
        className="flex w-max gap-3 group-hover:[animation-play-state:paused] motion-reduce:[animation:none] md:gap-4"
        style={{ animation: "ts-marquee 40s linear infinite" }}
      >
        {loop.map((a, i) => (
          <Tile key={`${a.name}-${i}`} a={a} />
        ))}
      </div>
    </div>
  );
}
